<template>
  <component
    :is="isSortable ? Reorder.Item : rootTag"
    ref="rootRef"
    :as="isSortable ? rootTag : null"
    class="list-item-standard"
    :class="itemClasses"
    :value="isSortable ? sortableId : null"
    :role="rootTag === 'a' ? null : 'listitem'"
    :href="resolvedHref"
    :target="props.href ? props.target : null"
    :tabindex="isClickable ? (props.tabindex ?? '0') : null"
    :drag-listener="isSortable ? !hasDragHandle : null"
    :drag-controls="isSortable && hasDragHandle ? dragControls : null"
    :style="itemStyle"
    @click="onClick"
    @keyup="onKeyup"
  >
    <div
      v-if="clickable"
      ref="blur-target-ref"
      v-ripple
      class="focus-helper"
    />

    <ListSection
      v-if="isSortable && hasDragHandle"
      class="list-item--drag-handle"
      side
      :drag-controls="dragControls"
      @pointerdown="onDrag"
    >
      <slot name="drag-handle">
        <q-icon name="mono-drag-handle" />
      </slot>
    </ListSection>
    <template v-if="!hasDefaultSlot">
      <ListSection
        v-if="startIcon"
        class="list-item--start-icon"
        side
      >
        <q-icon :name="startIcon" />
      </ListSection>
      <ListSection
        v-if="label || caption"
        class="list-item--label"
      >
        <ListLabel
          v-if="label"
          class="list-item--label-title"
        >
          {{ label }}
        </ListLabel>
        <ListLabel
          v-if="caption"
          class="list-item--label-caption"
          caption
        >
          {{ caption }}
        </ListLabel>
      </ListSection>
      <ListSection
        v-if="endIcon"
        class="list-item--end-icon"
        side
      >
        <q-icon :name="endIcon" />
      </ListSection>
    </template>

    <slot />
  </component>
</template>

<script setup>
import { inject, useTemplateRef, ref, computed, useAttrs, useSlots, onMounted } from 'vue'
import { useLink } from 'vue-router'
import { cx } from '@/helpers'
import { animate, Reorder, useDragControls, useMotionValue } from 'motion-v'
import ListSection from './ListSection.vue'
import ListLabel from './ListLabel.vue'

defineOptions({ name: 'ListItem' })

const emit = defineEmits(['click'])
const props = defineProps({
  label: { type: String, default: null },
  caption: { type: String, default: null },
  startIcon: { type: String, default: null },
  endIcon: { type: String, default: null },
  tag: { type: String, default: 'div' },
  active: { type: Boolean, default: null },
  exact: { type: Boolean, default: false },
  clickable: { type: Boolean, default: false },
  tabindex: { type: [String, Number], default: null },
  focused: { type: Boolean, default: false },
  manualFocus: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  to: { type: [String, Object], default: null },
  href: { type: String, default: null },
  target: { type: String, default: null },
  replace: { type: Boolean, default: false },
  sortableId: { type: String, default: null }
})

const slots = useSlots()
const hasDefaultSlot = computed(() => !!slots.default)
const isDense = inject('isDense')
const isSortable = inject('isSortable')
const hasDragHandle = inject('hasDragHandle')
// const inactiveShadow = '0px 0px 0px rgba(0,0,0,0.8)'
const inactiveShadow = '0 0 0 0 rgba(0,0,0,0), 0 0 0 0 rgba(0,0,0,0)'
const activeShadow = '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)'

// Always call useLink (rules of hooks) — output is only used when props.to is set
const { href: routerHref, isActive, isExactActive, navigate } = useLink({
  to: computed(() => props.to || '/'),
  replace: computed(() => props.replace)
})
const dragControls = useDragControls()
const $attrs = useAttrs()
const y = useMotionValue(0)
const boxShadow = useMotionValue(inactiveShadow)

const blurTargetRef = useTemplateRef('blur-target-ref')

const rootRef = ref()
const isDragging = ref(false)
const itemStyle = computed(() => {
  if (!isSortable) return { ...$attrs.style }

  return { ...$attrs.style, y, boxShadow }
})

const rootTag = computed(() => (props.to || props.href) ? 'a' : props.tag)
const resolvedHref = computed(() => {
  if (props.to) return routerHref.value
  if (props.href) return props.href
  return null
})
const isRouterActive = computed(() => {
  if (!props.to) return false
  return props.exact ? isExactActive.value : isActive.value
})
const resolvedActive = computed(() => {
  if (props.active !== null) return props.active
  return isRouterActive.value
})
const isClickable = computed(() =>
  (props.clickable || !!props.to || !!props.href) && !props.disabled
)
const itemClasses = computed(() => cx(
  resolvedActive.value && 'is-active',
  isClickable.value && 'is-clickable',
  isDense.value && 'is-dense',
  isSortable.value && 'is-sortable',
  isDragging.value && 'is-dragging',
  hasDragHandle.value && 'has-drag-handle',
  props.disabled && 'is-disabled',
  props.focused && 'is-focused'
))

const onClick = e => {
  if (props.disabled) return

  if (isClickable.value) {
    if (blurTargetRef.value && !e.qAvoidFocus) {
      if (e.qKeyEvent && document.activeElement === rootRef.value) {
        blurTargetRef.value.focus()
      } else if (document.activeElement === blurTargetRef.value) {
        rootRef.value.focus()
      }
    }

    emit('click', e)
    if (props.to) navigate(e)
  }
}
const onKeyup = e => {
  if (!isClickable.value) return
  if (e.key !== 'Enter' && e.key !== ' ') return
  e.preventDefault()
  e.stopPropagation()
  rootRef.value?.dispatchEvent(new MouseEvent('click', e))
}
const onDrag = e => dragControls.start(e)

onMounted(() => {
  let isActive = false
  y.on('change', latest => {
    const wasActive = isActive
    if (latest !== 0) {
      isActive = true
      if (isActive !== wasActive) {
        // animate(boxShadow, '5px 5px 10px rgba(0,0,0,0.3)')
        animate(boxShadow, activeShadow, { duration: 0.2 }
        )
      }
    } else {
      isActive = false
      if (isActive !== wasActive) {
        animate(boxShadow, inactiveShadow, { duration: 0.2 })
      }
    }
  })
})
</script>

<style scoped>
@reference "#app-styles.css";

.list-item-standard {
  @apply relative flex flex-row flex-nowrap;
  @apply text-[12px] leading-3.5 text-inherit select-none px-lg py-sm min-h-8;
  @apply transition-colors duration-300 ;

  .focus-helper {
    @apply absolute top-0.5 bottom-0.5 left-2 right-2 opacity-0 pointer-events-none rounded-md;
    @apply transition-opacity duration-300 ease-out;

    &:before { @apply content-[''] absolute inset-0 rounded-[inherit] layer-highlight-faint; }
  }

  &.is-dense { @apply min-h-6 py-0.5; }
  &.is-sortable:not(.has-drag-handle) { @apply cursor-grab; }

  &.is-clickable,
  &.is-sortable {
    @apply select-none;
    &:focus, &:focus-visible { @apply outline-none; }
  }
  &.is-clickable {  @apply cursor-pointer; }
  &.is-active { @apply text-primary-mid; }
  &.is-disabled { @apply opacity-40 pointer-events-none; }

  &[href] { @apply no-underline text-inherit; }

  &.is-dense .focus-helper {
    @apply rounded-sm;
    &::before { @apply top-px bottom-px; }
  }
  &:is(.is-clickable, .is-sortable):not(.has-drag-handle):hover .focus-helper {
    @apply opacity-100;
  }
  &:has(.list-item--drag-handle:hover) {
    &::before {
      @apply layer-highlight-faint;
    }
  }
  &:focus::before { @apply layer-highlight-deep border edge-primary-faintest; }
  &:focus-visible::before { @apply layer-highlight-deep border edge-primary-faintest; }
  &:is(.is-clickable, .is-sortable):active::before { @apply layer-highlight-deep; }
  &.is-focused::before { @apply layer-highlight-deep; }

  .list-item--drag-handle {
    @apply cursor-grab pr-[1em];
    .q-icon { @apply w-[0.5em] }
  }
  .list-item--start-icon {}
  .list-item--end-icon {}

  /* trailing side/avatar: flip padding when preceded by a main section */
  &:deep(.list-section:not(.is-side):not(.is-avatar) ~ .list-section.is-side),
  &:deep(.list-section:not(.is-side):not(.is-avatar) ~ .list-section.is-avatar) {
    @apply pr-0 pl-lg items-end;
  }
}
</style>
